
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { calculateBehavioralScores, sigmaToPercentile } from '@/utils/scoreCalculations';
import { usePersonalityProfile } from '@/hooks/usePersonalityProfile';
import { BehavioralScores } from '@/types/resultsTypes';
import { calculateProfileMatch } from '@/utils/profileMatching';
import { sigmaToPercentile as sigmaToPercentileCalc } from '@/utils/personalityCalculations';
import { resolveProfileType } from '@/utils/resolveProfileType';

// Flag calculation function moved from WorkBehaviorTableRow
const calculateFlagCount = (submission: SurveySubmission, scores: any): number => {
  let flagCount = 0;
  
  if (!submission.scores) {
    return flagCount;
  }

  try {
    // Enhanced dual assessment detection
    const isDualAssessment = submission.scores.isDualAssessment || 
      (submission.scores.self && submission.scores.work) ||
      (submission.selected_words && typeof submission.selected_words === 'object' && submission.selected_words.self);

    if (!isDualAssessment) {
      return flagCount;
    }

    // Check for flags in A-B-C-D traits
    const traits = ['A', 'B', 'C', 'D'];
    const traitNames = ['Drive', 'Connection', 'Task', 'Order'];
    
    traits.forEach((trait, index) => {
      const selfPercentile = scores[`${trait.toLowerCase()}Percentile`];
      const workPercentile = scores[`${trait.toLowerCase()}WorkPercentile`];
      
      if (typeof selfPercentile === 'number' && typeof workPercentile === 'number') {
        const selfAbove50 = selfPercentile > 50;
        const workAbove50 = workPercentile > 50;
        
        if (selfAbove50 !== workAbove50) {
          flagCount++;
        }
      }
    });

    // Check word count change
    const selfWordCount = Array.isArray(submission.selected_words?.self) 
      ? submission.selected_words.self.length 
      : (Array.isArray(submission.selected_words_text?.self) ? submission.selected_words_text.self.length : 0);
    
    const workWordCount = Array.isArray(submission.selected_words?.work) 
      ? submission.selected_words.work.length 
      : (Array.isArray(submission.selected_words_text?.work) ? submission.selected_words_text.work.length : 0);

    if (selfWordCount > 0 && workWordCount > 0) {
      const percentChange = Math.abs((workWordCount - selfWordCount) / selfWordCount) * 100;
      if (percentChange > 50) {
        flagCount++;
      }
    }

    // Check low objectivity
    const objectivityScore = scores.objectivityModel3 ?? scores.objectivityModel2 ?? scores.objectivityModel1;
    if (typeof objectivityScore === 'number') {
      const objectivityPercentile = sigmaToPercentile(objectivityScore);
      if (objectivityPercentile < 25) {
        flagCount++;
      }
    }

  } catch (error) {
    console.error('Error in flag calculation:', error);
    return 0;
  }
  
  return flagCount;
};

interface CognitiveSubmission {
  id: string;
  full_name: string;
  email: string;
  overall_percentile: number;
  completed_at: string;
  created_at: string;
  time_remaining: number;
}

interface SurveySubmission {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  selected_words: any;
  selected_words_text: any;
  completed_at: string;
  created_at: string;
  company_id?: string | null;
}

interface IdealProfile {
  id: string;
  name: string;
  dominance_min: number;
  dominance_max: number;
  extraversion_min: number;
  extraversion_max: number;
  patience_min: number;
  patience_max: number;
  formality_min: number;
  formality_max: number;
  objectivity_min: number;
  objectivity_max: number;
  company_id?: string;
}

export const useBehavioralSubmissionData = (
  filteredSubmissions: SurveySubmission[],
  selectedIdealProfile: IdealProfile | null
) => {
  const [cognitiveData, setCognitiveData] = useState<CognitiveSubmission[]>([]);
  const [companiesData, setCompaniesData] = useState<Record<string, string>>({});
  
  // Use centralized super admin check hook (cached)
  const { data: isSuperAdmin = false } = useQuery({
    queryKey: ['super-admin-status'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data } = await supabase.rpc('is_super_admin', {
        _user_id: user.id
      });

      return !!data;
    },
    staleTime: Infinity, // Cache for entire session
    refetchOnWindowFocus: false,
  });

  // Fetch company names for super admins with caching
  useEffect(() => {
    const fetchCompanyNames = async () => {
      if (!isSuperAdmin) return;
      const companyIds = [...new Set(filteredSubmissions.map(sub => sub.company_id).filter(Boolean))];
      if (companyIds.length === 0) return;
      
      // Optimized: only select needed columns
      const { data: companies, error } = await supabase
        .from('companies')
        .select('id, name')
        .in('id', companyIds);
      
      if (!error && companies) {
        const companyMap = companies.reduce((acc, company) => {
          acc[company.id] = company.name;
          return acc;
        }, {} as Record<string, string>);
        setCompaniesData(companyMap);
      }
    };
    fetchCompanyNames();
  }, [filteredSubmissions, isSuperAdmin]);

  useEffect(() => {
    const fetchCognitiveData = async () => {
      const emails = filteredSubmissions.map(sub => sub.email);
      if (emails.length === 0) return;
      
      const { data, error } = await supabase
        .from('cognitive_submissions')
        .select('id, full_name, email, overall_percentile, completed_at, created_at, time_remaining')
        .in('email', emails)
        .order('created_at', { ascending: false });
      
      if (!error && data) {
        // Deduplicate: keep only the most recent per email
        const latestByEmail = data.reduce((acc, item) => {
          if (!acc[item.email]) {
            acc[item.email] = item;
          }
          return acc;
        }, {} as Record<string, typeof data[0]>);
        
        setCognitiveData(Object.values(latestByEmail));
      }
    };
    fetchCognitiveData();
  }, [filteredSubmissions]);

  const enhancedSubmissions = filteredSubmissions.map(submission => {
    // Handle cognitive-only submissions
    if ((submission as any).isCognitiveOnly) {
      return {
        ...submission,
        profileType: 'Cognitive Only',
        matchPercentage: 0,
        wordsCount: 0,
        cognitivePercentile: (submission as any).cognitivePercentile,
        aPercentile: null,
        bPercentile: null,
        cPercentile: null,
        dPercentile: null,
        ePercentile: null,
        flagCount: 0,
        personaGap: 0,
        selfAssessmentComplete: false,
        workAssessmentComplete: false,
        completedDate: new Date(submission.completed_at),
        companyName: submission.company_id ? companiesData[submission.company_id] : 'General'
      };
    }

    const scores = calculateBehavioralScores(submission.scores);
    
    // Use canonical profile type resolver
    const isM3Assessment = (submission as any).assessment_variant === 'model3';
    const profileType = resolveProfileType({
      scores: submission.scores,
      assessment_variant: (submission as any).assessment_variant
    });
    
    // Still need percentiles for match calculation and display
    let dominancePercentile: number;
    let extraversionPercentile: number;
    let patiencePercentile: number;
    let formalityPercentile: number;
    
    if (isM3Assessment) {
      const selfScores = submission.scores?.selfBehavior?.scores || submission.scores;
      dominancePercentile = selfScores?.dominancePercentile ?? 50;
      extraversionPercentile = selfScores?.extraversionPercentile ?? 50;
      patiencePercentile = selfScores?.patiencePercentile ?? 50;
      formalityPercentile = selfScores?.formalityPercentile ?? 50;
    } else {
      dominancePercentile = sigmaToPercentileCalc(scores?.aScore || 0);
      extraversionPercentile = sigmaToPercentileCalc(scores?.bScore || 0);
      patiencePercentile = sigmaToPercentileCalc(scores?.cScore || 0);
      formalityPercentile = sigmaToPercentileCalc(scores?.dScore || 0);
    }

    let matchPercentage = 0;
    if (selectedIdealProfile) {
      const profileCompanyId = selectedIdealProfile.company_id;
      const submissionCompanyId = submission.company_id;
      const canMatch = isSuperAdmin || !profileCompanyId || profileCompanyId === submissionCompanyId;
      if (canMatch) {
        matchPercentage = calculateProfileMatch(scores, selectedIdealProfile);
      }
    }

    // Enhanced word count calculation to handle dual assessment structure
    let wordsCount = 0;
    
    // Check if this is dual assessment data (object with self/work properties)
    if (submission.selected_words && typeof submission.selected_words === 'object' && submission.selected_words.self) {
      // Dual assessment: use only self assessment word count
      wordsCount = Array.isArray(submission.selected_words.self) ? submission.selected_words.self.length : 0;
    } else if (submission.selected_words_text && typeof submission.selected_words_text === 'object' && submission.selected_words_text.self) {
      // Dual assessment fallback: use self from selected_words_text
      wordsCount = Array.isArray(submission.selected_words_text.self) ? submission.selected_words_text.self.length : 0;
    } else if (Array.isArray(submission.selected_words)) {
      // Single assessment: use array length
      wordsCount = submission.selected_words.length;
    } else if (Array.isArray(submission.selected_words_text)) {
      // Single assessment fallback: use selected_words_text array length
      wordsCount = submission.selected_words_text.length;
    }

    const cognitiveMatch = cognitiveData.find(cog => cog.email === submission.email);
    const cognitivePercentile = cognitiveMatch?.overall_percentile || null;
    
    // Calculate cognitive duration from timer (time_remaining), not timestamps
    let cognitiveDuration: number | null = null;
    if (cognitiveMatch) {
      const timeLimitSeconds = 12 * 60; // default 12 minutes
      cognitiveDuration = Math.max(0, timeLimitSeconds - (cognitiveMatch.time_remaining ?? 0));
    }

    // For E trait, always use Model 3 if available (for dashboard)
    const eScore =
      typeof scores.objectivityModel3 === "number" && !isNaN(scores.objectivityModel3)
        ? scores.objectivityModel3
        : (typeof scores.objectivityModel1 === "number" && !isNaN(scores.objectivityModel1)
          ? scores.objectivityModel1
          : (typeof scores.objectivityModel2 === "number" && !isNaN(scores.objectivityModel2) ? scores.objectivityModel2 : 0)
        );
    const ePercentile = sigmaToPercentile(eScore);

    // Calculate flag count for sorting
    const flagCount = calculateFlagCount(submission, scores);

    // Calculate persona gap - average difference between work and self traits
    const calculatePersonaGap = (): number => {
      const isDualAssessment = submission.scores?.isDualAssessment || 
        (submission.scores?.self && submission.scores?.work) ||
        (submission.selected_words && typeof submission.selected_words === 'object' && submission.selected_words.self);

      if (!isDualAssessment) return 0;

      const traits = ['a', 'b', 'c', 'd'];
      const differences: number[] = [];
      
      traits.forEach(trait => {
        const selfPercentile = scores[`${trait}Percentile`];
        const workPercentile = scores[`${trait}WorkPercentile`];
        
        if (typeof selfPercentile === 'number' && typeof workPercentile === 'number') {
          differences.push(Math.abs(selfPercentile - workPercentile));
        }
      });
      
      return differences.length > 0 ? differences.reduce((sum, diff) => sum + diff, 0) / differences.length : 0;
    };

    const personaGap = calculatePersonaGap();

    // Check if assessments are complete
    const isDualAssessment = submission.scores?.isDualAssessment || 
      (submission.scores?.self && submission.scores?.work) ||
      (submission.selected_words && typeof submission.selected_words === 'object' && submission.selected_words.self);
    
    const selfAssessmentComplete = isDualAssessment && 
      (submission.scores?.self || (submission.selected_words?.self && Array.isArray(submission.selected_words.self)));
    
    const workAssessmentComplete = isDualAssessment && 
      (submission.scores?.work || (submission.selected_words?.work && Array.isArray(submission.selected_words.work)));

    return {
      ...submission,
      profileType,
      matchPercentage,
      wordsCount,
      cognitivePercentile,
      cognitiveDuration,
      aPercentile: scores.aPercentile,
      bPercentile: scores.bPercentile,
      cPercentile: scores.cPercentile,
      dPercentile: scores.dPercentile,
      ePercentile, // Now Model 3 (reverse scoring)
      flagCount,
      personaGap,
      selfAssessmentComplete,
      workAssessmentComplete,
      completedDate: new Date(submission.completed_at),
      companyName: submission.company_id ? companiesData[submission.company_id] : 'General'
    };
  });

  return {
    enhancedSubmissions,
    isSuperAdmin,
    companiesData
  };
};
